import { Trans, useLingui } from "@lingui/react/macro";
import { Suspense } from "react";

import { cn } from "@/utils/shadcn-ui";

import type { GDMTResult } from "../../../api";

import { Badge } from "../../../components/ui-kit/base/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui-kit/base/popover";
import { Spinner } from "../../../components/ui-kit/spinner";
import { missingForScore, optimizationColor, optimizedPercent } from "./gdmt";
import { GdmtBreakdown } from "./gdmt-breakdown";
import { GdmtScoreRing } from "./gdmt-score-ring";

export function GdmtIndicator({ result }: { result: GDMTResult }) {
  const { t, i18n } = useLingui();
  const missing = missingForScore(result);
  if (missing) {
    return (
      <Popover>
        <PopoverTrigger
          className="border-none bg-transparent p-0 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
          aria-label={t`GDMT score not available, view details`}
        >
          <Badge variant="outline" className="text-muted-foreground">
            <Trans>GDMT n/a</Trans>
          </Badge>
        </PopoverTrigger>

        <PopoverContent align="start" side="bottom" className="w-64">
          <div className="mb-1 font-semibold">
            <Trans>GDMT Optimization</Trans>
          </div>
          <p className="text-muted-foreground text-sm">{i18n._(missing)}</p>
        </PopoverContent>
      </Popover>
    );
  }

  const percent = optimizedPercent(result);
  const color = optimizationColor(percent);

  return (
    <Popover>
      <PopoverTrigger
        className="rounded-full border-none bg-transparent p-0 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
        aria-label={t`GDMT score ${result.gdmt_score} of ${result.gdmt_max}, view breakdown`}
      >
        <GdmtScoreRing
          score={result.gdmt_score}
          max={result.gdmt_max}
          size={40}
        />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        avoidCollisions={false}
        collisionPadding={8}
        className="max-h-[min(var(--radix-popover-content-available-height),32rem)] w-[min(var(--radix-popover-content-available-width),480px)] overflow-y-auto"
      >
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold">
            <Trans>GDMT Optimization</Trans>
          </span>
          {result.hf_type && (
            <Badge variant="secondary">{result.hf_type}</Badge>
          )}
          <span
            className={cn("ml-auto shrink-0 text-sm font-bold", color.text)}
          >
            <Trans>{percent}% optimized</Trans>
          </span>
        </div>

        {/* The breakdown reads the medications catalog, which suspends the
            first time a session needs it. The header renders on pages that
            never load that catalog, so without a boundary here the popover
            would suspend the whole patient route behind it — same reason
            `ui-kit/dialog` carries one. */}
        <Suspense
          fallback={
            <div className="flex min-h-32 items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <GdmtBreakdown result={result} />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}
